import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { SENSOR_STATUS_LABELS, SENSOR_TYPE_LABELS } from '../../types/lawnTypes';

/**
 * Sensor detail panel: identity, battery, latest moisture, and a small moisture
 * trend drawn from the recent readings the data layer supplies.
 */

const formatTimestamp = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/** Inline sparkline; avoids pulling a chart library into a prototype panel. */
function MoistureSparkline({ readings }) {
  const path = useMemo(() => {
    const values = readings
      .map((reading) => reading.moisturePct)
      .filter((value) => value !== null && value !== undefined);

    if (values.length < 2) return null;

    const width = 220;
    const height = 56;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;

    const points = values.map((value, index) => {
      const x = (index / (values.length - 1)) * width;
      const y = height - ((value - min) / span) * (height - 8) - 4;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });

    return { line: `M ${points.join(' L ')}`, width, height, min, max };
  }, [readings]);

  if (!path) {
    return <p className="lawn-detail-muted">Not enough readings yet for a trend.</p>;
  }

  return (
    <div className="lawn-sparkline">
      <svg
        viewBox={`0 0 ${path.width} ${path.height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Moisture trend between ${path.min}% and ${path.max}%`}
      >
        <path d={path.line} fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
      <div className="lawn-sparkline-scale">
        <span>{path.min}%</span>
        <span>{path.max}%</span>
      </div>
    </div>
  );
}

MoistureSparkline.propTypes = {
  readings: PropTypes.array.isRequired,
};

export default function SensorDetail({
  sensor,
  readings,
  isLoadingReadings,
  onClose,
  onRename,
  onRemove,
}) {
  const [draftName, setDraftName] = useState(sensor?.name || '');

  useEffect(() => {
    setDraftName(sensor?.name || '');
  }, [sensor?.id, sensor?.name]);

  if (!sensor) {
    return (
      <p className="lawn-detail-muted">
        Select a pin on the property view to see its readings here.
      </p>
    );
  }

  const nameChanged = draftName.trim() && draftName.trim() !== sensor.name;

  return (
    <div className="lawn-detail">
      <div className="lawn-detail-head">
        <div>
          <span className={`lawn-detail-status lawn-detail-status-${sensor.status}`}>
            {SENSOR_STATUS_LABELS[sensor.status] || sensor.status}
          </span>
          <h3>{sensor.name}</h3>
          <p className="lawn-detail-muted">
            {SENSOR_TYPE_LABELS[sensor.type] || sensor.type}
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            className="lawn-detail-close"
            onClick={onClose}
            aria-label="Close sensor detail"
          >
            <i className="ri-close-line" aria-hidden="true"></i>
          </button>
        )}
      </div>

      <dl className="lawn-detail-stats">
        <div>
          <dt>Moisture</dt>
          <dd>{sensor.moisturePct === null ? '—' : `${sensor.moisturePct}%`}</dd>
        </div>
        <div>
          <dt>Battery</dt>
          <dd>{sensor.batteryPct}%</dd>
        </div>
        <div>
          <dt>Last reading</dt>
          <dd>{formatTimestamp(sensor.lastReadingAt)}</dd>
        </div>
        <div>
          <dt>Position</dt>
          <dd>
            {Math.round(sensor.xPct)}%, {Math.round(sensor.yPct)}%
          </dd>
        </div>
      </dl>

      <div className="lawn-detail-trend">
        <h4>Moisture trend</h4>
        {isLoadingReadings ? (
          <p className="lawn-detail-muted">Loading readings…</p>
        ) : (
          <MoistureSparkline readings={readings} />
        )}
      </div>

      <div className="lawn-detail-edit">
        <label htmlFor={`lawn-rename-${sensor.id}`}>Rename</label>
        <div className="lawn-detail-edit-row">
          <input
            id={`lawn-rename-${sensor.id}`}
            type="text"
            value={draftName}
            maxLength={40}
            onChange={(event) => setDraftName(event.target.value)}
          />
          <button
            type="button"
            className="green-button"
            disabled={!nameChanged}
            onClick={() => onRename(sensor.id, draftName.trim())}
          >
            Save
          </button>
        </div>
      </div>

      <button
        type="button"
        className="lawn-detail-remove"
        onClick={() => onRemove(sensor.id)}
      >
        <i className="ri-delete-bin-line" aria-hidden="true"></i> Remove this sensor
      </button>
    </div>
  );
}

SensorDetail.propTypes = {
  sensor: PropTypes.object,
  readings: PropTypes.array,
  isLoadingReadings: PropTypes.bool,
  onClose: PropTypes.func,
  onRename: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

SensorDetail.defaultProps = {
  sensor: null,
  readings: [],
  isLoadingReadings: false,
  onClose: null,
};
